/** Wire types for the operator usage dashboard. Cross-workspace, aggregate only. */

import type { WorkspacePlan } from './billing.js';
import type { SignupSource } from './signupSource.js';

/**
 * Cumulative counts as of a point in time — never counts *within* a window.
 *
 * That is what makes `totals` minus `previousTotals` a well-defined delta: both
 * are the same snapshot taken at two cutoffs. Windowed counts live in
 * `GrowthPoint` series instead.
 */
export interface UsageTotals {
  users: number;
  workspaces: number;
  quizzes: number;
  sessionsStarted: number;
  sessionsCompleted: number;
  /**
   * Votes cast inside recorded sessions. Vote counts are not indexed, so this
   * sums session snapshots rather than every COUNT# item — it covers sessions
   * recorded after the metrics deploy, not all time.
   */
  votesInSessions: number;
}

/** One point on a weekly series. `weekStart` is the Monday, `YYYY-MM-DD`. */
export interface GrowthPoint {
  weekStart: string;
  count: number;
}

export interface ActiveUsers {
  today: number;
  thisWeek: number;
  thisMonth: number;
  /**
   * Users with no activity in the current or previous calendar month.
   *
   * Deliberately not named for a day count: the boundary is calendar months,
   * so the real span slides between roughly 30 and 62 days depending on when
   * the query runs. Calling it `inactive30Days` would promise a rolling window
   * this does not compute. A true rolling window would cost 30 daily-partition
   * queries instead of two monthly ones, which is not worth it for this tile.
   */
  dormant: number;
}

/**
 * The adoption metrics the dashboard reports. A union rather than `string` so a
 * typo fails at compile time in either the producer or a consumer, the same way
 * `ConnectionView` pins the WebSocket view names.
 */
export type FeatureAdoptionKey =
  | 'hasCustomTheme'
  | 'hasLogo'
  | 'hasBackgroundImage'
  | 'multiScreen'
  | 'everPresented'
  | 'teamWorkspace'
  | 'inviteAccepted';

export interface FeatureAdoption {
  key: FeatureAdoptionKey;
  label: string;
  numerator: number;
  denominator: number;
  /** 0-100, rounded. Zero when the denominator is zero. */
  percentage: number;
}

export interface UsageOverview {
  totals: UsageTotals;
  /** The same totals as of the start of the window, for deltas. */
  previousTotals: UsageTotals;
  signupsPerWeek: GrowthPoint[];
  sessionsPerWeek: GrowthPoint[];
  actives: ActiveUsers;
  adoption: FeatureAdoption[];
  /** How many polls are being presented right now, across every workspace. */
  liveNow: number;
}

export interface WorkspaceUsageRow {
  workspaceId: string;
  name: string;
  members: number;
  quizzes: number;
  /**
   * Sessions started, matching `UsageTotals.sessionsStarted` so the column sums
   * to the KPI above it. Completed sessions are not broken out per workspace.
   */
  sessionsStarted: number;
  votesInSessions: number;
  createdAt: string;
  /** Latest of: session started, poll updated, member joined. Null if none. */
  lastActiveAt: string | null;
  /**
   * Whether this workspace has any poll live — a boolean, not a count.
   *
   * Named apart from `UsageOverview.liveNow` on purpose: that one counts live
   * polls platform-wide, so a workspace running two at once lights one lamp
   * here while contributing two there. The names must not invite a reader to
   * expect the table to sum to the banner.
   */
  isLiveNow: boolean;
  /**
   * What this workspace is entitled to, as a label rather than a billing
   * record. No Stripe ids reach this table — the closing line under it is a
   * promise about how deep the operator view goes, and a plan label keeps it.
   */
  plan: WorkspacePlan;
  /**
   * Whether a Stripe subscription is standing behind this workspace as well.
   *
   * Only interesting alongside `plan: 'granted'`. A grant outranks any real
   * renewal date, so a comped subscriber derives `source: 'grant'` and the
   * subscription would otherwise be invisible to the one person deciding
   * whether to revoke. Revoking is safe either way — it clears only
   * `grantUntil` — but the operator should be able to see that rather than
   * having to trust it. Backed by `subscriptionStanding`, so it excludes
   * exactly `ENDED_SUBSCRIPTION_STATUSES` rather than a hand-restated list
   * here — this comment named only two of those statuses once already, and
   * drifted from the real set the moment a third was added to it.
   */
  subscribed: boolean;
}

/**
 * The console actions worth counting.
 *
 * The array is the definition and the union is derived from it, rather than
 * the other way round. Both are needed — the union to make a typo a compile
 * error in either the producer or a consumer, the array because every
 * consumer iterates it (the repo builds one `evt_*` attribute per key, the
 * reader rebuilds counts from it, the dashboard ranks by it). Written as two
 * independent declarations they can drift: `readonly AdminEventKey[]` rejects
 * an *extra* member but says nothing about a *missing* one, so dropping a key
 * from the array while leaving it in the union compiles clean and the metric
 * silently reads zero forever. `ADMIN_EVENT_LABELS` below is exhaustiveness
 * -checked and would nag about a missing label, which makes the trap worse
 * rather than better: the one guardrail a developer meets is the one that
 * does not cover the array. Deriving removes the second source of truth.
 *
 * Console only. The taker and big-screen views are deliberately absent: those
 * are anonymous audience devices, and folding a room of 400 phones into the
 * same counters would drown every signal about the handful of people who
 * actually hold accounts. "What are my users clicking" is a question about
 * account holders.
 *
 * Names describe what the operator did, not which component fired — a button
 * can move without invalidating a year of history.
 */
export const ADMIN_EVENT_KEYS = [
  'pollCreated',
  'pollOpened',
  'pollDuplicated',
  'pollEdited',
  'presentationStarted',
  'presentationEnded',
  'screenAdvanced',
  'resultsHeld',
  'resultsRevealed',
  'votingClosed',
  'resultsViewed',
  'resultsExported',
  'runOpened',
  'memberInvited',
  'themeEdited',
  'folderCreated',
  'workspaceSwitched',
] as const;

export type AdminEventKey = (typeof ADMIN_EVENT_KEYS)[number];

export function isAdminEventKey(value: unknown): value is AdminEventKey {
  return ADMIN_EVENT_KEYS.includes(value as AdminEventKey);
}

/**
 * Human labels, in the console's own vocabulary — "poll" and "vote", never the
 * `Quiz` identifiers the code keeps. Lives in shared rather than in the web
 * package so the label and the key are defined in one place and a new key
 * cannot ship without one.
 */
export const ADMIN_EVENT_LABELS: Record<AdminEventKey, string> = {
  pollCreated: 'Created a poll',
  pollOpened: 'Opened a poll',
  pollDuplicated: 'Duplicated a poll',
  pollEdited: 'Saved poll edits',
  presentationStarted: 'Started presenting',
  presentationEnded: 'Ended presenting',
  screenAdvanced: 'Changed screen',
  resultsHeld: 'Held results',
  resultsRevealed: 'Revealed results',
  votingClosed: 'Closed or reopened voting',
  resultsViewed: 'Viewed results',
  resultsExported: 'Exported results',
  runOpened: 'Opened a past run',
  memberInvited: 'Invited a member',
  themeEdited: 'Edited the theme',
  folderCreated: 'Created a folder',
  workspaceSwitched: 'Switched workspace',
};

/**
 * One point on a daily series. `day` is `YYYY-MM-DD`.
 *
 * Deliberately not a `GrowthPoint` with a day stuffed into `weekStart`: that
 * field names a Monday, and a reader who trusts the name would bucket 56 daily
 * points into 8 weeks and get nonsense. Different granularity, different type.
 */
export interface DailyPoint {
  day: string;
  count: number;
}

/** One action's share of the counted total, over the whole window. */
export interface EventBreakdown {
  key: AdminEventKey;
  label: string;
  count: number;
  /** 0-100, rounded. Share of `EventsOverview.total`, zero when that is zero. */
  percentage: number;
}

/**
 * Console activity over a fixed span of days.
 *
 * Aggregate only, matching the contract at the top of this file: counts are
 * summed across every workspace and every user before they leave the API.
 * There is no per-user clickstream to ask for, by construction.
 */
export interface EventsOverview {
  /** Oldest to newest, one entry per day, zero-filled — never sparse. */
  perDay: DailyPoint[];
  /** Ranked most to least. Actions never counted in the window are omitted. */
  breakdown: EventBreakdown[];
  /** Every counted action in the window. */
  total: number;
  /**
   * Mean per day across `perDay`, including days that saw nothing.
   *
   * Carries one decimal below 10 and is a whole number at or above it. A plain
   * round reports "0 a day" for anything under half an action a day, which is
   * both the state a young product is actually in and the exact case this
   * dashboard exists to watch climb — and it sits next to a total, so "0 a day
   * on average" beside "20 actions" reads as a bug rather than as a slow week.
   */
  averagePerDay: number;
  /** The single busiest day in the window, or null when nothing was counted. */
  busiestDay: DailyPoint | null;
  /** How many days `perDay` spans, so a caller need not measure the array. */
  windowDays: number;
}

/**
 * One channel's share of the signups counted in the window.
 *
 * `key` rather than `source`, matching `EventBreakdown` and `FeatureAdoption`
 * above: the three are read by the same ledger component, and a field renamed
 * per type would mean a mapping step at every call site that exists only to
 * rename it.
 */
export interface SignupSourceBreakdown {
  key: SignupSource;
  label: string;
  count: number;
  /** 0-100, rounded. Share of `SignupOverview.total`, zero when that is zero. */
  percentage: number;
}

/**
 * Where accounts came from over a fixed span of days.
 *
 * Aggregate only, and more strictly so than the rest of this file: the rows
 * behind it hold integers per day and never a user id, so this cannot be
 * narrowed to a person even by an operator who wants to. See
 * `repos/signupSources.ts` for why that is the design rather than a policy.
 *
 * **`total` does not reconcile with `UsageOverview.signupsPerWeek`.** That
 * series counts user rows back to `METRICS_EPOCH`; these counters only start
 * at `SIGNUP_COUNTING_FROM`, so an overlapping window agrees and a longer one
 * does not. Same shape of caveat as `UsageTotals.votesInSessions`.
 */
export interface SignupOverview {
  /** Oldest to newest, one entry per day, zero-filled — never sparse. */
  perDay: DailyPoint[];
  /** Ranked most to least. Channels with no signups in the window are omitted. */
  bySource: SignupSourceBreakdown[];
  /** Every signup counted in the window. */
  total: number;
  /** How many days `perDay` spans, so a caller need not measure the array. */
  windowDays: number;
}
